/**
 * <feature-columns>
 * Progressive enhancement for the Feature Columns section's mobile carousel.
 *
 * The carousel itself is pure CSS scroll-snap (works without JS). This custom
 * element layers on:
 *   - keyboard navigation + ARIA state on the swipeable track, and
 *   - mobile pagination dots that reflect the active snap position, are
 *     tappable, and appear only when the cards overflow the viewport.
 *
 * The dots are derived from the track's real scroll metrics, so they map to
 * the existing scroll-snap positions (no separate slider library) and are
 * automatically hidden on desktop, where the grid fits and nothing overflows.
 *
 * Scoped to this section; the script tag is only rendered when the section is
 * on the page, and the custom element re-initialises automatically in the
 * Theme Editor because connectedCallback fires on every section render.
 */
if (!customElements.get('feature-columns')) {
  customElements.define(
    'feature-columns',
    class FeatureColumns extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('.feature-columns__grid');
        if (!this.track || this.dataset.carousel !== 'true') return;

        this.onKeydown = this.onKeydown.bind(this);
        this.track.addEventListener('keydown', this.onKeydown);

        // --- Pagination dots ---
        this.pagination = this.querySelector('.feature-columns__pagination');
        this.dots = [];
        this.activeIndex = -1;
        this.rafId = null;
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);

        if (this.pagination) {
          this.buildDots();
          this.track.addEventListener('scroll', this.onScroll, { passive: true });
          if ('ResizeObserver' in window) {
            this.resizeObserver = new ResizeObserver(this.onResize);
            this.resizeObserver.observe(this.track);
          } else {
            window.addEventListener('resize', this.onResize);
          }
        }
      }

      disconnectedCallback() {
        if (this.track) {
          this.track.removeEventListener('keydown', this.onKeydown);
          this.track.removeEventListener('scroll', this.onScroll);
        }
        if (this.resizeObserver) this.resizeObserver.disconnect();
        else window.removeEventListener('resize', this.onResize);
        if (this.rafId) cancelAnimationFrame(this.rafId);
      }

      /** Distance to move per step: one card + gap. */
      get step() {
        const item = this.track.querySelector('.feature-columns__item');
        if (!item) return this.track.clientWidth;
        const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
        return item.getBoundingClientRect().width + gap;
      }

      /** Number of snap positions. 0 means everything fits (no pagination). */
      get pageCount() {
        const max = this.track.scrollWidth - this.track.clientWidth;
        if (max <= 1) return 0;
        return Math.round(max / this.step) + 1;
      }

      onKeydown(event) {
        switch (event.key) {
          case 'ArrowRight':
            event.preventDefault();
            this.track.scrollBy({ left: this.step, behavior: 'smooth' });
            break;
          case 'ArrowLeft':
            event.preventDefault();
            this.track.scrollBy({ left: -this.step, behavior: 'smooth' });
            break;
          case 'Home':
            event.preventDefault();
            this.track.scrollTo({ left: 0, behavior: 'smooth' });
            break;
          case 'End':
            event.preventDefault();
            this.track.scrollTo({ left: this.track.scrollWidth, behavior: 'smooth' });
            break;
          default:
            break;
        }
      }

      /** (Re)build the dots when the number of snap positions changes. */
      buildDots() {
        const count = this.pageCount;
        if (count === this.dots.length) {
          this.updateActiveDot();
          return;
        }

        this.pagination.textContent = '';
        this.dots = [];
        this.activeIndex = -1;

        if (count <= 1) {
          this.pagination.setAttribute('hidden', '');
          return;
        }
        this.pagination.removeAttribute('hidden');

        for (let i = 0; i < count; i += 1) {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'feature-columns__dot';
          dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
          dot.addEventListener('click', () => {
            this.track.scrollTo({ left: i * this.step, behavior: 'smooth' });
          });
          this.pagination.appendChild(dot);
          this.dots.push(dot);
        }
        this.updateActiveDot();
      }

      /** Reflect the current scroll position in the active dot. */
      updateActiveDot() {
        if (!this.dots.length) return;
        const index = Math.max(
          0,
          Math.min(this.dots.length - 1, Math.round(this.track.scrollLeft / this.step))
        );
        if (index === this.activeIndex) return;

        const previous = this.dots[this.activeIndex];
        if (previous) {
          previous.classList.remove('is-active');
          previous.removeAttribute('aria-current');
        }
        this.activeIndex = index;
        this.dots[index].classList.add('is-active');
        this.dots[index].setAttribute('aria-current', 'true');
      }

      onScroll() {
        if (this.rafId) return;
        this.rafId = requestAnimationFrame(() => {
          this.rafId = null;
          this.updateActiveDot();
        });
      }

      onResize() {
        // Card widths / overflow change with the viewport (e.g. crossing the
        // desktop breakpoint), so recompute how many dots are needed.
        this.buildDots();
      }
    }
  );
}
